import { useState } from "react";
import { Button, Input, Text, Textarea, makeStyles, mergeClasses } from "@fluentui/react-components";
import type { LiteSession } from "../services/apiService";
import { submitContactRequest } from "../services/apiService";
import { Footer, IconTile, ShieldIcon, useUiStyles } from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

interface ContactPageProps {
  session: LiteSession;
}

const useStyles = makeStyles({
  formCard: {
    padding: "22px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  control: {
    width: "100%",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "4px",
  },
});

export function ContactPage({ session }: ContactPageProps) {
  const s = useUiStyles();
  const styles = useStyles();
  const { t } = useLanguage();
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const submit = async () => {
    if (!subject.trim() || !details.trim()) return;
    setSubmitting(true);
    setStatus("idle");
    try {
      await submitContactRequest(session.jwt, subject.trim(), details.trim());
      setSubject("");
      setDetails("");
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={s.page}>
      <div className={mergeClasses(s.card, s.accentTop)}>
        <div className={s.row}>
          <IconTile variant="brand"><ShieldIcon /></IconTile>
          <div className={s.grow}>
            <Text size={500} weight="semibold" block>{t("contact.title")}</Text>
            <Text size={200} block style={{ marginTop: "4px", color: "var(--colorNeutralForeground2)" }}>
              {t("contact.subtitle")}
            </Text>
          </div>
        </div>
      </div>

      <section className={s.section} style={{ marginTop: "18px" }}>
        <div className={mergeClasses(s.card, s.accentLeft, s.stack, styles.formCard)}>
          <label className={styles.field}>
            <Text size={200} weight="semibold">{t("contact.subject")}</Text>
            <Input className={styles.control} value={subject} maxLength={200} onChange={(_, data) => setSubject(data.value)} />
          </label>
          <label className={styles.field}>
            <Text size={200} weight="semibold">{t("contact.message")}</Text>
            <Textarea className={styles.control} value={details} resize="vertical" rows={6} onChange={(_, data) => setDetails(data.value)} />
          </label>
          <Text size={100} block style={{ color: "var(--colorNeutralForeground3)" }}>
            {t("contact.replyNote")}
          </Text>
          {status === "success" && <Text size={200} style={{ color: "var(--colorPaletteGreenForeground1)" }}>{t("contact.success")}</Text>}
          {status === "error" && <Text size={200} style={{ color: "var(--colorPaletteRedForeground1)" }}>{t("contact.error")}</Text>}
          <div className={styles.actions}>
            <Button appearance="primary" onClick={() => void submit()} disabled={submitting || !subject.trim() || !details.trim()}>
              {t("contact.send")}
            </Button>
          </div>
        </div>
      </section>
      <Footer>{t("homeFooter")}</Footer>
    </main>
  );
}